import type { VisibilityRange } from '../types';

export const VISIBILITY_RANGE_OPTIONS: Array<{ value: VisibilityRange; label: string }> = [
  { value: 'today', label: 'امروز' },
  { value: 'yesterday', label: 'دیروز' },
  { value: '7d', label: '۷ روز گذشته' },
  { value: '30d', label: '۳۰ روز گذشته' },
  { value: '90d', label: '۹۰ روز گذشته' },
  { value: 'all', label: 'همه زمان‌ها' },
  { value: 'custom', label: 'بازه دلخواه' },
];

export const DEFAULT_VISIBILITY_RANGE: VisibilityRange = '30d';

const VALID_RANGES: VisibilityRange[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'all',
  'custom',
];

export function isVisibilityRange(value: string): value is VisibilityRange {
  return (VALID_RANGES as string[]).includes(value);
}

function parseDateOnlyUtc(day: string): Date {
  const [year, month, dayNum] = day.split('-').map((part) => Number(part));
  return new Date(Date.UTC(year, month - 1, dayNum));
}

function startOfDayUtc(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0),
  );
}

function endOfDayUtc(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999),
  );
}

export function getVisibilityRangeBounds(
  range: VisibilityRange,
  customFrom: string,
  customTo: string,
): { start: Date | null; end: Date | null } {
  const now = new Date();

  switch (range) {
    case 'today':
      return { start: startOfDayUtc(now), end: endOfDayUtc(now) };
    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setUTCDate(yesterday.getUTCDate() - 1);
      return { start: startOfDayUtc(yesterday), end: endOfDayUtc(yesterday) };
    }
    case '7d': {
      const start = startOfDayUtc(now);
      start.setUTCDate(start.getUTCDate() - 6);
      return { start, end: endOfDayUtc(now) };
    }
    case '30d': {
      const start = startOfDayUtc(now);
      start.setUTCDate(start.getUTCDate() - 29);
      return { start, end: endOfDayUtc(now) };
    }
    case '90d': {
      const start = startOfDayUtc(now);
      start.setUTCDate(start.getUTCDate() - 89);
      return { start, end: endOfDayUtc(now) };
    }
    case 'custom': {
      const start = customFrom ? startOfDayUtc(parseDateOnlyUtc(customFrom)) : null;
      const end = customTo ? endOfDayUtc(parseDateOnlyUtc(customTo)) : null;
      return { start, end };
    }
    default:
      return { start: null, end: null };
  }
}

export function formatVisibilityRangeLabel(
  range: VisibilityRange,
  customFrom: string,
  customTo: string,
): string {
  if (range === 'custom' && customFrom && customTo) {
    const fmt = (iso: string) =>
      new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    return `${fmt(customFrom)} – ${fmt(customTo)}`;
  }

  const { start, end } = getVisibilityRangeBounds(range, customFrom, customTo);
  if (range !== 'all' && range !== 'custom' && start && end) {
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    if (range === 'today' || range === 'yesterday') {
      return VISIBILITY_RANGE_OPTIONS.find((o) => o.value === range)?.label ?? range;
    }
    return `${fmt(start)} – ${fmt(end)}`;
  }

  return VISIBILITY_RANGE_OPTIONS.find((o) => o.value === range)?.label ?? 'همه زمان‌ها';
}

export interface DateRangeQueryOptions {
  range?: VisibilityRange;
  from?: string;
  to?: string;
  topicId?: string;
  providerIds?: string[];
  brandKeys?: string[];
  sourceIds?: string[];
}

export function buildDateRangeQueryOptions(
  range: VisibilityRange,
  customFrom: string,
  customTo: string,
  extras?: Omit<DateRangeQueryOptions, 'range' | 'from' | 'to'>,
): DateRangeQueryOptions {
  return {
    range,
    ...(range === 'custom' && customFrom && customTo
      ? { from: customFrom, to: customTo }
      : {}),
    ...extras,
  };
}

export function isWithinVisibilityRange(
  iso: string | null | undefined,
  range: VisibilityRange,
  customFrom: string,
  customTo: string,
): boolean {
  if (!iso) return range === 'all';
  const { start, end } = getVisibilityRangeBounds(range, customFrom, customTo);
  if (!start && !end) return true;
  const date = new Date(iso);
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

export function matchesModelFilter(
  modelFilterId: string | null,
  ...modelIds: Array<string | null | undefined>
): boolean {
  if (!modelFilterId) return true;
  return modelIds.some((id) => id === modelFilterId);
}
